/**
 * ErrorPage — 500 server error page.
 * Uses react-i18next for reactive language switching.
 * Design: Warm Editorial Minimal, centered card, no sidebar.
 *
 * @layer-2 — domain composition
 */
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Logo } from "@/shared/components/Logo";
import { CopyrightFooter } from "@/shared/components/CopyrightFooter";
import { FixedLanguageToggle } from "@/shared/components/FixedLanguageToggle";
import { cn, toArabicNumerals } from "@/shared/lib/utils";
import { authPalette } from "@/shared/lib/design-tokens/colors";
import { usePageDirection } from "@/shared/hooks/usePageDirection";

function OrnamentDivider({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-4 animate-scaleXIn", className)}>
      <div className="flex-1 h-px" style={{ background: "rgba(26,25,23,0.10)" }} />
      <div className="w-1.5 h-1.5 rounded-full" style={{ background: authPalette.terracotta }} />
      <div className="flex-1 h-px" style={{ background: "rgba(26,25,23,0.10)" }} />
    </div>
  );
}

export function ErrorPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const dir = usePageDirection();
  const errorCodeDisplay = dir === "rtl" ? toArabicNumerals(500) : "500";

  return (
    <>
      <FixedLanguageToggle />
      <div
        className="min-h-screen flex flex-col items-center justify-center p-6"
        style={{ background: authPalette.warmPaper }}
      >
        <main className="w-full max-w-[480px]">
          <div className="text-center mb-8 animate-fadeUp">
            <div className="flex justify-center mb-6">
              <Logo height={48} />
            </div>
            <OrnamentDivider className="mt-6" />
          </div>

          <div
            className="rounded-2xl overflow-hidden text-center animate-fadeUp"
            style={{
              background: authPalette.white,
              border: "1px solid",
              borderColor: authPalette.borderLight,
              boxShadow: authPalette.cardShadow,
            }}
          >
            <div className="pt-10 pb-4">
              <p
                className="text-7xl font-bold leading-none"
                style={{ fontFamily: "'Libre Bodoni', serif", color: authPalette.ink }}
                dir="ltr"
              >
                {errorCodeDisplay}
              </p>
            </div>

            <div className="px-6 pb-2">
              <h2
                className="text-xl font-bold mb-2"
                style={{ fontFamily: "'Libre Bodoni', serif", color: authPalette.ink }}
                dir={dir}
              >
                {t("common.errors.serverError")}
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: authPalette.textOnLight }} dir={dir}>
                {t("common.errors.serverErrorDescription")}
              </p>
            </div>

            <div className="mx-6 my-6 h-px" style={{ background: authPalette.divider }} />

            <div className="px-6 pb-8 flex flex-col gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                className={cn(
                  "w-full h-12 rounded-xl font-bold text-[11px] uppercase tracking-[0.18em]",
                  "flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer",
                )}
                style={{
                  background: authPalette.ink,
                  color: authPalette.white,
                  boxShadow: authPalette.cardShadow,
                  fontFamily: "'Public Sans', sans-serif",
                }}
              >
                {t("common.errors.goToDashboard")}
              </button>

              <button
                onClick={() => navigate("/login")}
                className="text-sm font-semibold transition-colors duration-150 cursor-pointer"
                style={{ color: authPalette.textOnLight }}
                dir={dir}
              >
                {t("common.errors.backToLogin")}
              </button>
            </div>
          </div>

          <div
            className="mt-8 text-center animate-fadeIn"
            style={{ animationDelay: "0.24s" }}
          >
            <CopyrightFooter className="text-[9px] uppercase tracking-[0.22em] font-bold" />
          </div>
        </main>
      </div>
    </>
  );
}